import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle, HeartPulse, ShieldCheck } from 'lucide-react';

const MISSION = '#1A5276';
const TEAL = '#148F77';
const GOLD = '#D4AC0D';
const URGENT = '#C0392B';

const CRISIS_FACTS = [
  {
    stat: '1 in 10',
    title: 'Filipinos are affected by CKD',
    detail: 'Many people are diagnosed late, when treatment options are already limited and costs are higher.',
    color: MISSION,
  },
  {
    stat: '₱3,500–₱7,000',
    title: 'Average cost per dialysis session',
    detail: 'Most patients need three sessions every week just to stay alive and stable.',
    color: TEAL,
  },
  {
    stat: 'Only 31%',
    title: 'Receive dialysis consistently',
    detail: 'Coverage gaps and household poverty force families to skip lifesaving treatment.',
    color: URGENT,
  },
] as const;

export function TheCrisisPage(): JSX.Element {
  return (
    <main id="main-content" className="min-h-screen bg-gray-50 font-sans text-[#1C2833]" role="main" aria-label="The Crisis">
      <section className="relative overflow-hidden text-white" style={{ background: `linear-gradient(180deg, ${MISSION} 0%, #0D3349 100%)` }}>
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white">
            ← Back to Home
          </Link>
          <p className="mt-7 text-xs font-bold tracking-[0.35em] uppercase" style={{ color: GOLD }}>
            The Crisis
          </p>
          <h1 className="mt-4 max-w-4xl text-3xl font-extrabold leading-tight sm:text-4xl lg:text-5xl">
            Chronic Kidney Disease is a financial and medical emergency for Filipino families.
          </h1>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-white/90">
            Every missed dialysis session increases risk of complications, hospitalization, and death. Families are often
            forced to choose between treatment and basic needs.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {CRISIS_FACTS.map((fact) => (
            <article key={fact.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-4xl font-black sm:text-5xl" style={{ color: fact.color }}>
                {fact.stat}
              </p>
              <h2 className="mt-3 text-lg font-bold text-[#1C2833]">{fact.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{fact.detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-2">
          <article className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#1A5276]/10">
              <AlertTriangle className="h-5 w-5" style={{ color: MISSION }} aria-hidden="true" />
            </div>
            <h2 className="mt-4 text-2xl font-bold text-[#1C2833]">Why families fall behind</h2>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-slate-700">
              <li>Dialysis is recurrent, urgent, and expensive.</li>
              <li>Lost income from caregiving compounds medical costs.</li>
              <li>Transportation and medicine costs are often unpaid and unplanned.</li>
              <li>Coverage support is partial, delayed, or exhausted.</li>
            </ul>
          </article>

          <article className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#148F77]/10">
              <HeartPulse className="h-5 w-5" style={{ color: TEAL }} aria-hidden="true" />
            </div>
            <h2 className="mt-4 text-2xl font-bold text-[#1C2833]">What immediate support changes</h2>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-slate-700">
              <li>Patients keep their dialysis schedule and avoid critical interruptions.</li>
              <li>Families can protect food, housing, and school budgets.</li>
              <li>Emergency admissions and severe complications are reduced.</li>
              <li>Patients regain stability to work, parent, and recover dignity.</li>
            </ul>
          </article>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="rounded-2xl border border-[#1A5276]/20 bg-white p-6 shadow-sm sm:p-8">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#D4AC0D]/20">
            <ShieldCheck className="h-5 w-5 text-[#8A6F08]" aria-hidden="true" />
          </div>
          <h2 className="mt-4 text-2xl font-bold text-[#1C2833] sm:text-3xl">How GSGO responds to the crisis</h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-700 sm:text-base">
            GSGO connects donors to verified patient needs, coordinates disbursement with local operations, and reports impact
            with transparency. The goal is simple: keep patients in treatment now while helping families build resilience.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to="/donate#donationwidget"
              className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-bold text-[#1C2833] transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: GOLD }}
            >
              Give Monthly — Save a Life
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/warriors"
              className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
              style={{ color: TEAL }}
            >
              Meet the CKD Warriors
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
